import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { Subject, Subscription, of } from 'rxjs';
import { debounceTime, switchMap, catchError } from 'rxjs/operators';
import { BusinessService, BusinessSearchResult } from '../../services/business.service';
import { ConfigService } from '../../services/config.service';

interface ServiceFilter {
  key: string;
  label: string;
  icon: string;
  active: boolean;
}

@Component({
  selector: 'app-pedir',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule],
  templateUrl: './pedir.component.html',
  styleUrl: './pedir.component.scss'
})
export class PedirComponent implements OnInit, OnDestroy {
  searchQuery = '';
  results: BusinessSearchResult[] = [];
  loading = false;
  searched = false;
  showFilters = false;

  serviceFilters: ServiceFilter[] = [
    { key: 'takeaway', label: 'Recoger', icon: '🥡', active: false },
    { key: 'delivery', label: 'Delivery', icon: '🛵', active: false },
    { key: 'reservation', label: 'Reservar', icon: '📅', active: false },
  ];

  private searchSubject = new Subject<string>();
  private sub?: Subscription;

  constructor(
    private router: Router,
    private businessService: BusinessService,
    private configService: ConfigService
  ) {}

  ngOnInit(): void {
    this.sub = this.searchSubject.pipe(
      debounceTime(400),
      switchMap(q => {
        this.loading = true;
        this.searched = true;
        return this.businessService.searchBusinesses(q, this.activeFilterParams).pipe(
          catchError(() => {
            this.loading = false;
            return of([]);
          })
        );
      })
    ).subscribe(results => {
      this.results = results;
      this.loading = false;
    });

    // Auto-search on init (load all)
    this.doSearch();
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  onSearchInput(): void {
    this.searchSubject.next(this.searchQuery);
  }

  doSearch(): void {
    this.searchSubject.next(this.searchQuery);
  }

  toggleFilter(f: ServiceFilter): void {
    f.active = !f.active;
    this.doSearch();
  }

  toggleFilters(): void {
    this.showFilters = !this.showFilters;
  }

  get activeFilterCount(): number {
    return this.serviceFilters.filter(f => f.active).length;
  }

  get activeFilterParams(): { takeaway?: boolean; delivery?: boolean; reservation?: boolean } {
    const params: any = {};
    for (const f of this.serviceFilters) {
      if (f.active) params[f.key] = true;
    }
    return params;
  }

  // Navigate to business detail (shows takeaway/delivery/reserve options)
  goToBusiness(biz: BusinessSearchResult): void {
    this.router.navigate(['/business', biz.id]);
  }

  canOrder(biz: BusinessSearchResult): boolean {
    return biz.allows_takeaway || biz.allows_delivery;
  }

  canReserve(biz: BusinessSearchResult): boolean {
    return biz.accepts_reservations;
  }

  getCapabilities(biz: BusinessSearchResult): string[] {
    const caps: string[] = [];
    if (biz.allows_takeaway) caps.push('Recoger');
    if (biz.allows_delivery) caps.push('Delivery');
    if (biz.accepts_reservations) caps.push('Reservar');
    return caps;
  }

  getBusinessTypeIcon(type?: string): string {
    switch (type) {
      case 'restaurante': case 'restaurant': return '🍽️';
      case 'peluqueria': case 'salon': return '💇';
      case 'bar': return '🍺';
      case 'cafeteria': case 'cafe': return '☕';
      default: return '🏪';
    }
  }

  resolveImg(url: string | undefined | null): string {
    return this.configService.resolveImageUrl(url);
  }

  goBack(): void {
    this.router.navigate(['/dashboard']);
  }
}
